import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Loader2, Mail, X, RotateCcw, Send, Download } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { cn } from "../lib/utils";
import { tenantLocale, tenantTz, useBranding } from "../lib/company";


function _fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(tenantLocale(), { timeZone: tenantTz(),
      day: "numeric", month: "short", year: "numeric",
      hour: "numeric", minute: "2-digit", hourCycle: "h23",
    });
  } catch {
    return iso;
  }
}


// Feb 2026 refit — the previous `_buildResendBody` helper prepended
// "Re-sending... which the assessor was unable to locate" on every
// re-send. Dispatcher preference: keep the email brief and
// professional, no meta-commentary. Body is now identical for first
// sends and re-sends (backend `_suggest_body` is the single source
// of truth).


/**
 * One-click email-the-insurer flow. Replaces the previous "download ZIP then
 * drag-drop into Outlook" workflow with a single Send button that goes through
 * Resend, attaching the PDF + each photo individually so the assessor sees
 * inline thumbnails in their inbox.
 *
 * Props:
 *   open / onOpenChange  — controlled visibility
 *   job                  — the full job object (for title display)
 *   onSent(result)       — optional callback after successful send
 */
export default function EmailInsurerDialog({ open, onOpenChange, job, onSent }) {
  const branding = useBranding();
  const signOff = branding?.salvage_entity_full || branding?.trading_name || "";
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);
  const [recipients, setRecipients] = useState([]);   // list of {email}
  const [recipientInput, setRecipientInput] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [replyTo, setReplyTo] = useState("");

  // Load preview each time the dialog opens for a new job.
  useEffect(() => {
    if (!open || !job?.id) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/jobs/${job.id}/email-insurer/preview`)
      .then((r) => {
        if (cancelled) return;
        setPreview(r.data);
        // If this job was emailed before, auto-pre-fill the previous
        // recipient so a re-send is one click. Body stays the same as
        // a first-send (backend's `_suggest_body` is the single source
        // of truth — no more passive-aggressive "assessor couldn't find
        // the original" prefix).
        const last = r.data?.last_sent;
        const lastTo = Array.isArray(last?.to) ? last.to : [];
        setSubject(last?.subject || r.data.subject || "");
        setRecipients(lastTo.map((email) => ({ email })));
        setBody(r.data.body || "");
        setReplyTo(r.data.reply_to_default || "");
        setRecipientInput("");
      })
      .catch((e) => {
        toast.error(e?.response?.data?.detail || "Couldn't load email preview");
        onOpenChange(false);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [open, job?.id, onOpenChange]);

  const photoCount = preview?.photo_count ?? 0;
  const savedInsurers = preview?.saved_insurers ?? [];

  // Allow free-text recipient entry too — chip when user hits Enter or comma.
  function commitRecipientInput() {
    const raw = recipientInput.trim().replace(/,$/, "");
    if (!raw) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw)) {
      toast.error(`"${raw}" doesn't look like a valid email`);
      return;
    }
    if (recipients.some((r) => r.email.toLowerCase() === raw.toLowerCase())) {
      setRecipientInput("");
      return;
    }
    setRecipients((cur) => [...cur, { email: raw }]);
    setRecipientInput("");
  }

  function addSavedInsurer(ins) {
    if (recipients.some((r) => r.email.toLowerCase() === ins.email.toLowerCase())) return;
    setRecipients((cur) => [...cur, { email: ins.email, name: ins.name }]);
  }

  function removeRecipient(idx) {
    setRecipients((cur) => cur.filter((_, i) => i !== idx));
  }

  async function handleSendNow() {
    if (recipientInput.trim()) commitRecipientInput();
    const finalRecipients = recipients
      .concat(recipientInput.trim() ? [{ email: recipientInput.trim() }] : []);
    if (!finalRecipients.length) {
      toast.error("Add at least one recipient");
      return;
    }
    setSending(true);
    // Feb 2026 refit — kick off the background send task, then poll
    // for status. Replaces the direct blocking POST that was hitting
    // Cloudflare's 100 s edge timeout on large photo bundles and
    // showing dispatchers a "could not parse" page.
    let progressToastId = null;
    try {
      const { data } = await api.post(
        `/jobs/${job.id}/email-insurer/send`,
        {
          to: finalRecipients.map((r) => r.email),
          subject,
          body,
          reply_to: replyTo || null,
        },
      );
      const taskId = data?.task_id;
      if (!taskId) throw new Error("Server did not return a task id");
      progressToastId = toast.loading("Preparing photos and PDF…");
      // Poll every 2 s for up to 5 minutes. Backend keeps updating its
      // status doc; we stop on sent/failed or on timeout.
      const start = Date.now();
      const TIMEOUT_MS = 5 * 60_000;
      const POLL_MS = 2000;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise((r) => setTimeout(r, POLL_MS));
        const { data: t } = await api.get(
          `/jobs/${job.id}/email-insurer/task/${taskId}`,
        );
        if (t?.progress) {
          toast.loading(t.progress, { id: progressToastId });
        }
        if (t?.status === "sent") {
          const n = t.attachment_count || 0;
          const recips = (t.to || []).join(", ");
          toast.success(
            `Sent to ${recips} · ${n} attachment${n === 1 ? "" : "s"} · Resend ID ${(t.resend_id || "").slice(0, 8) || "?"}`,
            { id: progressToastId, duration: 6000 },
          );
          onSent?.({ sent: true, resend_id: t.resend_id, attachment_count: n });
          onOpenChange(false);
          return;
        }
        if (t?.status === "failed") {
          toast.error(t.error || "Send failed", { id: progressToastId, duration: 12000 });
          return;
        }
        if (Date.now() - start > TIMEOUT_MS) {
          toast.error(
            "Send is taking longer than 5 minutes — check the Sent folder before retrying.",
            { id: progressToastId, duration: 12000 },
          );
          return;
        }
      }
    } catch (e) {
      const detail = e?.response?.data?.detail
        || (e?.code === "ECONNABORTED" ? "Timed out sending the email — try again."
        : e?.message || "Send failed");
      if (progressToastId) toast.error(detail, { id: progressToastId });
      else toast.error(detail);
    } finally {
      setSending(false);
    }
  }

  async function handlePrepareEml() {
    // Fallback path — kept for offline or edge-case handling. The
    // browser downloads a .eml file; user double-clicks to open it
    // in their local mail client. WARNING: on Outlook (Windows Mail
    // app) the .eml opens as a viewed message with no Send button —
    // dispatchers should use "Send now" instead in almost every case.
    if (recipientInput.trim()) commitRecipientInput();
    const finalRecipients = recipients
      .concat(recipientInput.trim() ? [{ email: recipientInput.trim() }] : []);
    if (!finalRecipients.length) {
      toast.error("Add at least one recipient");
      return;
    }
    setSending(true);
    try {
      const res = await api.post(
        `/jobs/${job.id}/email-insurer`,
        {
          to: finalRecipients.map((r) => r.email),
          subject,
          body,
          reply_to: replyTo || null,
        },
        {
          responseType: "blob",
          timeout: 90_000,
        },
      );
      const attachmentCount = Number(res.headers?.["x-attachment-count"] || 0);
      const disp = res.headers?.["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/i.exec(disp);
      const filename = match ? match[1] : `Insurer-Email-${(job.rego || "job").toUpperCase()}.eml`;
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "message/rfc822" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(
        `Draft downloaded (${attachmentCount} attachment${attachmentCount === 1 ? "" : "s"}). ` +
        `NOTE: if Outlook opens it with no Send button, use "Send now" instead.`,
        { duration: 10000 },
      );
      onSent?.({ prepared_eml: true, filename, attachment_count: attachmentCount });
      onOpenChange(false);
    } catch (e) {
      let detail = "Email preparation failed";
      if (e?.code === "ECONNABORTED" || /timeout/i.test(e?.message || "")) {
        detail = "Timed out preparing the email. Please try again.";
      } else {
        try {
          const text = await e?.response?.data?.text?.();
          if (text) detail = JSON.parse(text).detail || detail;
        } catch (_) { /* ignore */ }
      }
      toast.error(detail);
    } finally {
      setSending(false);
    }
  }

  const headlineVehicle = useMemo(() => {
    const v = job?.vehicle || "";
    const r = (job?.rego || "").toUpperCase();
    if (v && r) return `${v} (${r})`;
    return v || r || "Job";
  }, [job]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="email-insurer-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-blue-600" />
            Email insurer — {headlineVehicle}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Prepare an .eml draft with the Vehicle Incident Report PDF and photos, then open it in your local mail app (Outlook / Apple Mail / Thunderbird) to send from your own account.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 flex items-center justify-center text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <div className="space-y-4">
            {/* ---- Send-count box (Aug 2026) — always visible so the billing user /
                dispatch see at a glance how many times this insurer has
                already been emailed for this job before hitting Send. Zero
                is fine; > 0 gets a stronger colour so it stands out. ---- */}
            {(() => {
              const totalSends = preview?.total_sends || 0;
              const isMulti = totalSends > 0;
              return (
                <div
                  className={cn(
                    "rounded-md border px-3 py-2 flex items-center justify-between",
                    isMulti
                      ? "border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30"
                      : "border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40"
                  )}
                  data-testid="email-send-count-box"
                >
                  <div className="flex items-center gap-2">
                    <Mail className={cn(
                      "h-5 w-5",
                      isMulti ? "text-amber-700 dark:text-amber-300" : "text-slate-500"
                    )} />
                    <span className={cn(
                      "text-sm font-semibold",
                      isMulti ? "text-amber-900 dark:text-amber-200" : "text-slate-700 dark:text-slate-200"
                    )}>
                      Emailed so far
                    </span>
                  </div>
                  <span
                    className={cn(
                      "text-2xl font-black tabular-nums",
                      isMulti ? "text-amber-800 dark:text-amber-200" : "text-slate-400 dark:text-slate-500"
                    )}
                    data-testid="email-send-count-value"
                  >
                    {totalSends}
                    <span className="text-xs font-bold uppercase tracking-wider ml-1">
                      {totalSends === 1 ? "time" : "times"}
                    </span>
                  </span>
                </div>
              );
            })()}

            {/* ---- Previous-send banner (if this job was emailed before) ---- */}
            {preview?.last_sent && (
              <div
                className="rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-sm flex items-start gap-2"
                data-testid="last-sent-banner"
              >
                <RotateCcw className="h-4 w-4 text-amber-700 dark:text-amber-300 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <div className="font-semibold text-amber-900 dark:text-amber-200">
                    Previously sent {preview.total_sends > 1 ? `(${preview.total_sends} times) ` : ""}— most recently {_fmtDate(preview.last_sent.sent_at)}
                  </div>
                  <div className="text-xs text-amber-800 dark:text-amber-300 mt-0.5">
                    To: {(preview.last_sent.to || []).join(", ")} &middot; by {preview.last_sent.sent_by || "?"} &middot; {preview.last_sent.attachment_count} attachment{preview.last_sent.attachment_count === 1 ? "" : "s"}
                  </div>
                  <div className="text-xs text-amber-800 dark:text-amber-300 mt-1">
                    Recipient pre-filled below — just click <b>Send</b> to resend with the current photos.
                  </div>
                </div>
              </div>
            )}

            {/* ---- To: field with chips + saved insurer quick pick ---- */}
            <div>
              <Label className="text-sm font-semibold">To</Label>
              <div className="mt-1 flex flex-wrap items-center gap-1.5 min-h-[42px] rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1.5">
                {recipients.map((r, idx) => (
                  <span
                    key={`${r.email}-${idx}`}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-900 dark:text-blue-200 text-xs"
                    data-testid={`recipient-chip-${idx}`}
                  >
                    {r.name ? `${r.name} <${r.email}>` : r.email}
                    <button
                      type="button"
                      onClick={() => removeRecipient(idx)}
                      className="hover:text-rose-600"
                      aria-label="Remove recipient"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
                <input
                  type="email"
                  value={recipientInput}
                  onChange={(e) => setRecipientInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") {
                      e.preventDefault();
                      commitRecipientInput();
                    } else if (e.key === "Backspace" && !recipientInput && recipients.length) {
                      removeRecipient(recipients.length - 1);
                    }
                  }}
                  onBlur={commitRecipientInput}
                  placeholder={recipients.length ? "" : "assessor@insurer.co.nz, …"}
                  className="flex-1 min-w-[140px] bg-transparent text-sm outline-none px-1"
                  data-testid="recipient-input"
                />
              </div>
              {savedInsurers.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  <span className="text-xs text-slate-500 dark:text-slate-400 pt-1">Quick pick:</span>
                  {savedInsurers.map((ins) => (
                    <button
                      key={ins.id}
                      type="button"
                      onClick={() => addSavedInsurer(ins)}
                      className="text-xs px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-blue-100 dark:hover:bg-blue-900/40 text-slate-700 dark:text-slate-300 transition-colors"
                      data-testid={`quick-pick-${ins.id}`}
                      title={ins.email}
                    >
                      + {ins.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* ---- Subject ---- */}
            <div>
              <Label htmlFor="email-subject" className="text-sm font-semibold">Subject</Label>
              <Input
                id="email-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="mt-1"
                data-testid="email-subject"
              />
            </div>

            {/* ---- Body ---- */}
            <div>
              <Label htmlFor="email-body" className="text-sm font-semibold">Message</Label>
              <Textarea
                id="email-body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={9}
                className="mt-1 font-sans text-sm"
                data-testid="email-body"
              />
            </div>

            {/* ---- Reply-to ---- */}
            <div>
              <Label htmlFor="email-reply-to" className="text-sm font-semibold">Reply-to</Label>
              <Input
                id="email-reply-to"
                type="email"
                value={replyTo}
                onChange={(e) => setReplyTo(e.target.value)}
                placeholder="dispatcher@yourcompany.co.nz"
                className="mt-1"
                data-testid="email-reply-to"
              />
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                Insurer&apos;s reply lands here. Leave as your business email so you see their response.
              </p>
            </div>

            {/* ---- Attachment summary ---- */}
            <div className="rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 px-3 py-2 text-sm" data-testid="attachment-summary">
              <div className="font-semibold text-slate-700 dark:text-slate-200">Attachments</div>
              <ul className="mt-1 text-slate-600 dark:text-slate-400 space-y-0.5">
                <li>• Incident-Report-{(job?.customer_name || "Job").replace(/[^a-zA-Z0-9-_ ]/g, "").trim().slice(0, 40) || "Job"}.pdf</li>
                <li>
                  • {photoCount} damage photo{photoCount === 1 ? "" : "s"}
                  {photoCount > 0
                    ? ` (Photo-01 through Photo-${String(photoCount).padStart(2, "0")})`
                    : " — none on file"}
                </li>
              </ul>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 flex-wrap sm:flex-nowrap">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={handlePrepareEml}
            disabled={sending || loading}
            className="text-slate-600 dark:text-slate-400"
            data-testid="email-prepare-eml-btn"
            title="Fallback: download a .eml file to open in your local mail client. NOTE: Outlook may open it as a viewed message with no Send button — use 'Send now' instead for a real one-click send."
          >
            {sending ? (
              <><Loader2 className="h-4 w-4 animate-spin" /> Preparing…</>
            ) : (
              <><Download className="h-4 w-4" /> Download .eml (fallback)</>
            )}
          </Button>
          <Button
            onClick={handleSendNow}
            disabled={sending || loading}
            className="bg-emerald-600 hover:bg-emerald-700 text-white border border-emerald-700 dark:bg-emerald-600 dark:border-emerald-500 dark:hover:bg-emerald-500"
            data-testid="email-send-now-btn"
            title="Send directly via Resend. The insurer gets the email immediately, and a copy lands in your inbox."
          >
            {sending ? (
              <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</>
            ) : (
              <><Send className="h-4 w-4" /> Send now</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
